import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { fmtDate } from '../../lib/dates';

// Log a service that happened to a horse — "hocks injected, 12 Aug, Dr Chen" —
// with no document involved.
//
// This writes a health_records row, the same table VetRecordUpload saves to. The
// PDF path there hard-gates on choosing a file, so until now there was no way to
// record a visit you have no paperwork for, which is most of them. The POST
// endpoint already treated `document` as optional, so no API change was needed.
//
// Deliberately NOT the notes timeline: a service has a date, a provider and a
// due-date, and the dashboards read health_records to surface what is coming up.
// A note would be invisible to all of that.

const TYPE_OPTIONS = [
  ['vet', 'Vet'],
  ['injection', 'Injection'],
  ['farrier', 'Farrier'],
  ['dental', 'Dental'],
  ['vaccination', 'Vaccination'],
  ['coggins', 'Coggins'],
  ['deworming', 'Deworming'],
  ['injury', 'Injury'],
  ['other', 'Other'],
];

// Common jobs, to save typing on a phone in the aisle.
const SUGGESTIONS = {
  vet: ['Lameness exam', 'Wellness check', 'Ultrasound', 'Bloodwork'],
  injection: ['Hocks injected', 'Stifles injected', 'Coffin joints injected'],
  farrier: ['Reset shoes', 'New shoes', 'Trim'],
  dental: ['Teeth floated'],
  vaccination: ['Spring shots', 'Fall shots', 'Rabies', 'Flu/Rhino'],
  deworming: ['Dewormed'],
};

const COUNTS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const UNITS = [
  ['weeks', 'weeks'],
  ['months', 'months'],
  ['years', 'years'],
];

// Typical intervals, used to seed the pickers when you switch to "In …".
// Only applied on entering that mode — changing the type afterwards will not
// silently rewrite a choice you already made.
const DEFAULT_INTERVAL = {
  vet: ['6', 'months'],
  injection: ['6', 'months'],
  farrier: ['6', 'weeks'],
  dental: ['1', 'years'],
  vaccination: ['6', 'months'],
  coggins: ['1', 'years'],
  deworming: ['3', 'months'],
  injury: ['2', 'weeks'],
  other: ['6', 'months'],
};

// Date maths in UTC so a date string never drifts by a day across timezones —
// same approach the rest of the app uses for YYYY-MM-DD values.
function addDaysISO(iso, n) {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// Calendar-aware, with day clamping: 31 Jan + 1 month is 28/29 Feb, not 3 March.
// Years go through here too (n * 12), which also handles 29 Feb + 1 year.
function addMonthsISO(iso, n) {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.getUTCDate();
  d.setUTCDate(1); // avoid rolling into the next month while shifting
  d.setUTCMonth(d.getUTCMonth() + n);
  const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, lastDay));
  return d.toISOString().slice(0, 10);
}

/** Resolve the due date. Relative intervals count from the SERVICE date, not
 *  today — logging last week's farrier visit should still be due 6 weeks from
 *  the visit. */
function resolveDue({ mode, from, count, unit, on }) {
  if (mode === 'on') return on || '';
  if (mode !== 'in' || !from) return '';
  const n = Number(count);
  if (!Number.isFinite(n)) return '';
  if (unit === 'weeks') return addDaysISO(from, n * 7);
  if (unit === 'years') return addMonthsISO(from, n * 12);
  return addMonthsISO(from, n);
}

function formatLong(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return fmtDate(iso, 'dayShortYear');
}

const REMIND_OPTIONS = [
  ['', 'Don’t remind me'],
  ['3', '3 days before'],
  ['7', '1 week before'],
  ['14', '2 weeks before'],
  ['30', '1 month before'],
  ['60', '2 months before'],
];

// Pure ISO date maths — no timezone involved, matching how the server computes
// the fire date with SQLite's date(due_date, '-N days').
function remindOnDate(dueISO, days) {
  const d = new Date(`${dueISO}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '—';
  d.setUTCDate(d.getUTCDate() - Number(days));
  return d.toISOString().slice(0, 10);
}

function todayISO() {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

export default function ServiceRecordForm({ horseId, onDone, onCancel }) {
  const [recordType, setRecordType] = useState('vet');
  const [date, setDate] = useState(todayISO());
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [providerId, setProviderId] = useState('');
  const [newProviderName, setNewProviderName] = useState('');
  // Next due is either an interval from the service date ("in 6 months") or a
  // fixed calendar date. 'none' means nothing is scheduled.
  const [dueMode, setDueMode] = useState('none'); // 'none' | 'in' | 'on'
  const [dueCount, setDueCount] = useState('6');
  const [dueUnit, setDueUnit] = useState('months');
  const [dueOn, setDueOn] = useState('');
  const [remind, setRemind] = useState('14'); // days before; '' = don't remind
  const [err, setErr] = useState('');

  const dueDate = resolveDue({
    mode: dueMode,
    from: date,
    count: dueCount,
    unit: dueUnit,
    on: dueOn,
  });

  function changeDueMode(next) {
    setDueMode(next);
    if (next === 'in') {
      const [n, u] = DEFAULT_INTERVAL[recordType] ?? ['6', 'months'];
      setDueCount(n);
      setDueUnit(u);
    }
  }

  const providersQ = useQuery({
    queryKey: ['providers'],
    queryFn: () => api.get('/providers'),
  });
  const providers = providersQ.data?.providers ?? [];

  const save = useMutation({
    mutationFn: () =>
      api.post(`/horses/${horseId}/records`, {
        recordType,
        date,
        title: title.trim(),
        details: details.trim() || undefined,
        dueDate: dueDate || undefined,
        // Only meaningful with a due date; the API drops it otherwise anyway.
        remindDaysBefore: dueDate && remind !== '' ? Number(remind) : undefined,
        // The name is never sent: the server reads it off the provider record,
        // so the record and the directory can't drift apart.
        providerId: providerId && providerId !== '__new' ? providerId : undefined,
        newProviderName:
          providerId === '__new' ? newProviderName.trim() || undefined : undefined,
      }),
    onSuccess: onDone,
    onError: (e) => setErr(friendly(e)),
  });

  function submit(e) {
    e.preventDefault();
    setErr('');
    if (!title.trim()) return setErr('Say what was done — e.g. “Hocks injected”.');
    if (!date) return setErr('Pick a date.');
    if (providerId === '__new' && !newProviderName.trim()) {
      return setErr('Name the provider, or choose “No provider”.');
    }
    save.mutate();
  }

  const suggestions = SUGGESTIONS[recordType] ?? [];

  return (
    <form onSubmit={submit} className="mb-3 pb-4 border-b border-ink-100 space-y-3">
      <div>
        <label htmlFor="svc-what" className="block text-sm font-medium text-ink-700 mb-1">
          What was done
        </label>
        <input
          id="svc-what"
          className="input"
          placeholder="e.g. Hocks injected"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
        />
        {suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setTitle(s)}
                className="text-xs px-2 py-1 rounded-full bg-ink-100 text-ink-600 hover:bg-ink-200"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="svc-type" className="block text-sm font-medium text-ink-700 mb-1">
            Type
          </label>
          <select
            id="svc-type"
            className="input"
            value={recordType}
            onChange={(e) => setRecordType(e.target.value)}
          >
            {TYPE_OPTIONS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="svc-date" className="block text-sm font-medium text-ink-700 mb-1">
            Date
          </label>
          <input
            id="svc-date"
            type="date"
            className="input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
      </div>

      <div>
        <label htmlFor="svc-provider" className="block text-sm font-medium text-ink-700 mb-1">
          Provider
        </label>
        <select
          id="svc-provider"
          className="input"
          value={providerId}
          onChange={(e) => setProviderId(e.target.value)}
        >
          <option value="">
            {providersQ.isLoading ? 'Loading…' : 'No provider'}
          </option>
          {providers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}{p.businessName ? ` — ${p.businessName}` : ''}
            </option>
          ))}
          {/* Somebody writing up a visit from a farrier who isn't in the
              directory shouldn't have to abandon the form, go and add him, and
              start again. The server creates the record and links it, so the
              visit is still tied to a provider rather than to typed text. */}
          <option value="__new">+ Someone not in the list…</option>
        </select>

        {providerId === '__new' && (
          <input
            className="input mt-2"
            placeholder="Provider name"
            value={newProviderName}
            autoFocus
            onChange={(e) => setNewProviderName(e.target.value)}
          />
        )}

        {!providersQ.isLoading && providers.length === 0 && providerId !== '__new' && (
          <p className="text-xs text-ink-400 mt-1">
            No providers yet. Pick “Someone not in the list” to add one as you go.
          </p>
        )}
      </div>

      <div>
        <label htmlFor="svc-notes" className="block text-sm font-medium text-ink-700 mb-1">
          Notes <span className="font-normal text-ink-400">(optional)</span>
        </label>
        <textarea
          id="svc-notes"
          className="input"
          rows={2}
          placeholder="Anything worth remembering — dose, findings, follow-up"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="svc-due-mode" className="block text-sm font-medium text-ink-700 mb-1">
          Next due <span className="font-normal text-ink-400">(optional)</span>
        </label>

        <div className="flex flex-wrap items-center gap-2">
          <select
            id="svc-due-mode"
            className="input w-auto"
            value={dueMode}
            onChange={(e) => changeDueMode(e.target.value)}
          >
            <option value="none">Not scheduled</option>
            <option value="in">In…</option>
            <option value="on">On a specific date</option>
          </select>

          {dueMode === 'in' && (
            <>
              <select
                className="input w-auto"
                value={dueCount}
                onChange={(e) => setDueCount(e.target.value)}
                aria-label="How many"
              >
                {COUNTS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <select
                className="input w-auto"
                value={dueUnit}
                onChange={(e) => setDueUnit(e.target.value)}
                aria-label="Weeks, months or years"
              >
                {UNITS.map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </>
          )}

          {dueMode === 'on' && (
            <input
              type="date"
              className="input w-auto"
              value={dueOn}
              onChange={(e) => setDueOn(e.target.value)}
              aria-label="Due date"
            />
          )}
        </div>

        <p className="text-xs text-ink-400 mt-1">
          {dueMode === 'none'
            ? 'Set this and it shows up on the barn dashboard as it gets close.'
            : dueDate
              ? `Due ${formatLong(dueDate)}${dueMode === 'in' ? ' — counted from the service date' : ''}.`
              : 'Pick a date.'}
        </p>
      </div>

      {/* Reminders only make sense once there's a date to count back from. */}
      {dueDate && (
        <div className="rounded-md bg-ink-50 p-3">
          <label htmlFor="svc-remind" className="block text-sm font-medium text-ink-700 mb-1">
            Email reminder
          </label>
          <select
            id="svc-remind"
            className="input"
            value={remind}
            onChange={(e) => setRemind(e.target.value)}
          >
            {REMIND_OPTIONS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <p className="text-xs text-ink-400 mt-1">
            {remind === ''
              ? 'No email will be sent.'
              : `Emails the barn's owners and managers on ${remindOnDate(dueDate, remind)}.`}
          </p>
        </div>
      )}

      {err && <p className="text-sm text-red-600">{err}</p>}

      <div className="flex gap-2">
        <button type="submit" disabled={save.isPending} className="btn-primary">
          {save.isPending ? 'Saving…' : 'Save Service'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={save.isPending}
          className="text-sm text-ink-500 hover:text-ink-800 px-2"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function friendly(e) {
  switch (e?.body?.error || e?.message || '') {
    case 'missing_date':
      return 'Pick a date for the service.';
    case 'horse_not_found':
      return 'This horse wasn’t found in your barn.';
    case 'provider_not_found':
      return 'That provider is no longer in the directory — pick another.';
    case 'forbidden':
      return 'Only an owner or manager can add records.';
    // Everything the switch above doesn't name. It used to land here too, as
    // "Try again", which was advice rather than information and was wrong as
    // often as not — retrying a 404 forever will not fix a 404. The code and
    // the status are the only things that distinguish one unknown failure
    // from another, so they are what gets said.
    default: {
      const code = e?.body?.error || e?.message;
      const status = e?.status;
      if (!code && !status) return 'No response from the server. Check your connection.';
      return `Couldn’t save that${code ? `: ${code}` : ''}${status ? ` (${status})` : ''}.`;
    }
  }
}
